import React, {
  PropsWithChildren,
  useContext,
  useState,
  useCallback,
} from 'react';
import { axiosInstance } from 'src/api/client';
import { ReportsApiResponse } from 'src/api/types/api-types';
import { BASE_URL } from 'src/app/environment';
import { PlaceResult } from 'src/components/auto-complete/AutocompleteComponent';
import { getCityName } from 'src/utils/app/string';

type ContextValue = {
  isLoading: boolean;
  address: PlaceResult | undefined;
  radius: number;
  // eslint-disable-next-line no-unused-vars
  setRadiusData: (data: number) => void;
  // eslint-disable-next-line no-unused-vars
  setAddress: (addr: PlaceResult) => void;
  tags: string[];
  // eslint-disable-next-line no-unused-vars
  setTags: (tags: string[]) => void;
  callReportApi: () => Promise<void>;
};

export const Context = React.createContext<ContextValue | undefined>(undefined);

export const ReportsProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [address, setAddress] = useState<PlaceResult | undefined>();
  const [tags, setTags] = useState<string[]>([]);
  const [radius, setRadius] = useState<number>(4);

  const callReportApi = useCallback(async () => {
    if (!address) {
      return;
    }

    setIsLoading(true);

    //const place = await fetchPlaceDetails(address?.place_id)

    const params = {
      keywords: tags,
      city: getCityName(address),
      name: address.name,
      rating: address.rating,
      placeId: address.place_id,
      latitude: address.geometry?.location?.lat(),
      longitude: address.geometry?.location?.lng(),
      radius: radius * 1000,
    };

    const response = await axiosInstance.post<ReportsApiResponse>(
      `${BASE_URL}/reports/generateReport`,
      params,
    );
    setIsLoading(false);
    if (response.data.success && response.data.data?.token) {
      const data = response.data.data;
      window.open(`/analysis/?token=${data.token}`, '_blank');
    }
  }, [address, tags]);

  const setRadiusData = (data: number) => {
    setRadius(data);
  };

  return (
    <Context.Provider
      value={{
        radius,
        isLoading,
        address,
        setAddress,
        tags,
        setTags,
        callReportApi,
        setRadiusData,
      }}>
      {children}
    </Context.Provider>
  );
};

export const useReports = () => {
  const context = useContext(Context);

  if (context === undefined) {
    throw new Error('useReports must be used within a ReportsProvider');
  }

  return context;
};
